import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AllTemplatesForBuilderDocument,
  AssemblyTemplateField,
  TemplateTypeEnum,
  useAllTemplatesForBuilderQuery,
  useCloneTemplateFieldsMutation,
} from '@/__generated__/graphql';
import { useSnackBarMessage } from './utils';
import Box from '@mui/material/Box';
import InputLabel from '@mui/material/InputLabel';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import FormControl from '@mui/material/FormControl';
import CircularProgress from '@mui/material/CircularProgress';
import Chip from '@mui/material/Chip';
import MenuItem from '@mui/material/MenuItem';
import { SelectChangeEvent, Select } from '@mui/material';
import { GenericDialog } from '@/components/shared/GenericDialog';
import { textButtonStyle, containedButtonStyle } from '@/theme';

type AddTemplateFieldsToStructureDialogProps = {
  structureId: string;
  isOpen: boolean;
  onClose: () => void;
  structureName: string;
  currentStructureId: string;
};

export const AddTemplateFieldsToStructureDialog = ({
  structureId,
  isOpen,
  onClose,
  structureName,
  currentStructureId,
}: AddTemplateFieldsToStructureDialogProps) => {
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [allTemplateFields, setAllTemplateFields] = useState<AssemblyTemplateField[] | undefined>(
    []
  );

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });

  const { data: assemblyTemplateData } = useAllTemplatesForBuilderQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    if (assemblyTemplateData?.allTemplates && assemblyTemplateData.allTemplates.length > 0) {
      setAllTemplateFields(assemblyTemplateData?.allTemplates.at(0)?.fields);
    }
  }, [assemblyTemplateData]);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    setSelectedOptions(event.target.value as string[]);
  };

  const handleClose = useCallback(() => {
    setSelectedOptions([]);
    onClose();
  }, [onClose]);

  const [cloneTemplateFieldsMutation, { loading }] = useCloneTemplateFieldsMutation();

  const fieldIds = useMemo(() => {
    if (!allTemplateFields) return [];
    return allTemplateFields
      .filter((field) => selectedOptions.includes(field.name))
      .map((field) => field.id);
  }, [allTemplateFields, selectedOptions]);

  const addTemplateFieldsToStructure = useCallback(async () => {
    await cloneTemplateFieldsMutation({
      variables: {
        input: {
          type: TemplateTypeEnum.Assembly,
          name: 'Pipes',
          sourceStructureId: structureId,
          targetStructureId: currentStructureId,
          fieldIds,
        },
      },
      refetchQueries: [AllTemplatesForBuilderDocument],
      onCompleted: () => {
        onClose();
        showSnackBar(
          `${selectedOptions.join(
            ', '
          )} template field(s) have been cloned to ${structureName} successfully`,
          'success'
        );
      },
      onError: (error) => {
        console.error(error);
        onClose();
        showSnackBar(`${error.message}`, 'error');
      },
    });
  }, [
    cloneTemplateFieldsMutation,
    currentStructureId,
    structureId,
    fieldIds,
    onClose,
    showSnackBar,
    selectedOptions,
    structureName,
  ]);

  return (
    <GenericDialog
      open={isOpen}
      onClose={handleClose}
      showCloseIcon
      title={`Select template fields to add to ${structureName}`}
      content={
        <FormControl fullWidth>
          <InputLabel>Select Fields</InputLabel>
          <Select
            multiple
            value={selectedOptions}
            onChange={handleChange}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={selectedOptions.find((option) => option === value)}
                    color="primary"
                  />
                ))}
              </Box>
            )}
          >
            {allTemplateFields?.map((template) => (
              <MenuItem key={template.id} value={template.name}>
                <Checkbox checked={selectedOptions.includes(template.name)} />
                <ListItemText primary={template.name} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      }
      actions={
        <>
          <Button onClick={handleClose} variant="text" sx={textButtonStyle}>
            Cancel
          </Button>
          <Button
            onClick={addTemplateFieldsToStructure}
            sx={containedButtonStyle}
            disabled={loading}
            variant="contained"
          >
            {loading ? 'Copying' : 'Confirm'}
            {loading && <CircularProgress color="info" size={15} sx={{ ml: 1 }} />}
          </Button>
        </>
      }
      actionsStyles={{ mr: 3, pb: 3 }}
    />
  );
};
